class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-add-complement]');
      if (button) this.addComplementaryProduct(button);
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  async loadComplementaryProducts() {
    const container = this.querySelector('[data-cart-recommendations]');
    if (!container || container.dataset.loaded === 'true') return;
    container.dataset.loaded = 'true';

    const productId = container.dataset.productId;
    const excludedIds = new Set((container.dataset.cartProductIds || '').split(','));
    const list = container.querySelector('[data-cart-recommendations-list]');

    try {
      let response = await fetch(`${window.Shopify.routes.root}recommendations/products.json?product_id=${productId}&limit=10&intent=complementary`);
      let data = response.ok ? await response.json() : { products: [] };
      if (!data.products?.length) {
        response = await fetch(`${window.Shopify.routes.root}recommendations/products.json?product_id=${productId}&limit=10&intent=related`);
        data = response.ok ? await response.json() : { products: [] };
      }

      const products = (data.products || []).filter((product) => !excludedIds.has(String(product.id))).slice(0, 6);
      if (!products.length) {
        container.hidden = true;
        return;
      }

      list.innerHTML = products.map((product) => {
        const variant = product.variants.find((item) => item.available) || product.variants[0];
        const image = product.featured_image ? `<img src="${product.featured_image}${product.featured_image.includes('?') ? '&' : '?'}width=240" alt="" loading="lazy" width="120" height="120">` : '';
        return `<article class="mcp-cart-recommendation">
          <a class="mcp-cart-recommendation__image" href="${product.url}">${image}</a>
          <a class="mcp-cart-recommendation__name" href="${product.url}">${product.title}</a>
          <span class="mcp-cart-recommendation__price">${this.formatMoney(variant.price)}</span>
          <button class="mcp-cart-recommendation__add" type="button" data-add-complement="${variant.id}" ${variant.available ? '' : 'disabled'}>${variant.available ? 'Ajouter' : 'Épuisé'}</button>
        </article>`;
      }).join('');
    } catch (error) {
      container.hidden = true;
    }
  }

  formatMoney(cents) {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(cents) / 100);
  }

  async addComplementaryProduct(button) {
    if (button.disabled) return;
    button.disabled = true;
    button.textContent = 'Ajout…';
    try {
      const response = await fetch(`${window.Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(button.dataset.addComplement), quantity: 1 }],
          sections: this.getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname,
        }),
      });
      if (!response.ok) throw new Error('Cart add failed');
      const state = await response.json();
      state.id = Number(button.dataset.addComplement);
      this.renderContents(state);
    } catch (error) {
      button.disabled = false;
      button.textContent = 'Réessayer';
    }
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
